import { M_PER_DEG_LAT, metresPerDegLng, type LatLng } from '@/core/utils';
import { loadElevationSampler } from '@/features/elevation';
import { summarize, type ConeGrid } from '../model/result';
import type { ConeResult, GlideMode, GlideSettings } from '../model/types';
import { solve } from '../solver/solverClient';
import { cropElevationGrid, sampleElevationGrid } from './elevationGrid';

export interface ComputeRequest {
  origin: LatLng;
  mode: GlideMode;
  settings: GlideSettings;
  signal: AbortSignal;
  /** The DEM is loaded and the search over a size×size grid starts. */
  onSearching?: (size: number) => void;
}

/**
 * 1. Load the DEM around the origin and resample it onto a metric grid centred on it.
 * 2. Shrink the grid to what is physically reachable.
 * 3. Run the solver and summarise the result.
 * Throws an AbortError when `signal` fires between steps.
 */
export async function computeCone({
  origin,
  mode,
  settings,
  signal,
  onSearching,
}: ComputeRequest): Promise<ConeResult> {
  const { glideRatio, clearance, cellSize, maxRadius } = settings;
  const t0 = performance.now();

  const mPerDegLng = metresPerDegLng(origin.lat);
  const dLat = maxRadius / M_PER_DEG_LAT;
  const dLng = maxRadius / mPerDegLng;
  const sample = await loadElevationSampler(
    { n: origin.lat + dLat, s: origin.lat - dLat, e: origin.lng + dLng, w: origin.lng - dLng },
    cellSize,
  );
  signal.throwIfAborted();

  const full = sampleElevationGrid(sample, origin, cellSize, maxRadius, mPerDegLng);
  const { originElev, minElev, maxElev } = full;
  // Takeoff and landing are both on the ground.
  const baseAlt = originElev;
  // No glide can cover more ground than the available height difference allows.
  const heightRange = mode === 'fly' ? baseAlt - minElev : maxElev - originElev;
  const radius = Math.min(maxRadius, Math.max(cellSize, heightRange * glideRatio));
  const halfSize = Math.min(full.half, Math.ceil(radius / cellSize));
  const size = 2 * halfSize + 1;
  const elev = cropElevationGrid(full, halfSize);

  onSearching?.(size);
  const solved = await solve({
    mode,
    elev,
    size,
    cellSize,
    baseAlt,
    glideRatio,
    clearance,
    radiusCells: radius / cellSize,
    maxAlt: maxElev,
  });
  signal.throwIfAborted();

  const grid: ConeGrid = {
    ...solved,
    mode,
    origin,
    halfSize,
    size,
    cellSize,
    glideRatio,
    baseAlt,
    elev,
    originElev,
    mPerDegLng,
    radius,
    limited: radius >= maxRadius,
  };
  return { ...grid, ...summarize(grid), seconds: (performance.now() - t0) / 1000 };
}
